import { LockOutlined, PhoneOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input } from "antd";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { accountDeletionService } from "../services/accountDeletion.service";

interface FormValues {
	phone: string;
	password: string;
}

export function DeleteAccountForm() {
	const { t } = useTranslation("accountDeletion");
	const [form] = Form.useForm<FormValues>();
	const [natija, setNatija] = useState<string | null>(null);
	const [xato, setXato] = useState<string | null>(null);

	const { mutate, isPending } = useMutation({
		mutationFn: (values: FormValues) => accountDeletionService.request(values),
		onSuccess: (message) => {
			setXato(null);
			setNatija(message);
			form.resetFields();
		},
		onError: (error: unknown) => {
			setNatija(null);
			setXato(xatoMatni(error, t));
		},
	});

	const onFinish = (values: FormValues) => {
		setXato(null);

		// `LoginForm` bilan bir xil naqsh — maydon 9 xonali raqamni oladi,
		// backend esa `+998...` to'liq shaklini kutadi (`phoneSchema`).
		mutate({
			phone: `+998${values.phone.replace(/\s+/g, "")}`,
			password: values.password,
		});
	};

	if (natija) {
		return (
			<Alert
				type="success"
				showIcon
				title={t("form.success")}
				description={natija}
				className="mt-2"
			/>
		);
	}

	return (
		<Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} size="large">
			{xato ? <Alert type="error" showIcon title={xato} className="mb-4" /> : null}

			<Form.Item
				name="phone"
				label={t("form.phoneLabel")}
				rules={[
					{ required: true, message: t("form.phoneRequired") },
					{ pattern: /^\d{9}$/, message: t("form.phoneFormat") },
				]}
			>
				<Input
					prefix={<PhoneOutlined className="text-gray-400" />}
					addonBefore="+998"
					placeholder="901234567"
					autoComplete="tel"
					maxLength={9}
				/>
			</Form.Item>

			<Form.Item
				name="password"
				label={t("form.passwordLabel")}
				extra={t("form.passwordExtra")}
				rules={[{ required: true, message: t("form.passwordRequired") }]}
			>
				<Input.Password
					prefix={<LockOutlined className="text-gray-400" />}
					placeholder={t("form.passwordPlaceholder")}
					autoComplete="current-password"
				/>
			</Form.Item>

			<Form.Item className="!mb-0">
				<Button type="primary" danger htmlType="submit" loading={isPending} block>
					{t("form.submit")}
				</Button>
			</Form.Item>
		</Form>
	);
}

/**
 * ⚠️ 401 va 429 uchun ALOHIDA matn. Server 401 da har doim aynan bir xil
 * «Telefon raqami yoki parol noto'g'ri» xabarini beradi (hisob mavjudligini
 * oshkor qilmaslik uchun) — uni shu ko'rinishda ko'rsatamiz, boyitmaymiz.
 */
function xatoMatni(error: unknown, t: TFunction<"accountDeletion">): string {
	const status = (error as { response?: { status?: number } })?.response?.status;

	if (status === 401) {
		return t("form.errorInvalidCredentials");
	}

	if (status === 429) {
		return t("form.errorTooManyRequests");
	}

	return t("form.errorGeneric");
}
